/**
 * Playable source resolution for `VideoJSPlayer`.
 *
 * A video activity carries `delivery_details`, one entry per provider the
 * backend has published the asset to. This module turns those entries into the
 * `{ src, type }` list video.js consumes, ordered by preference.
 *
 * Both streaming providers hand us a bare id and a fixed URL template:
 *   cloudflare_stream  videodelivery.net/<id>/manifest/video.{mpd,m3u8}
 *   bunny_stream       vz-<zone>.b-cdn.net/<id>/{playlist.m3u8,thumbnail.jpg}
 */

/**
 * The `delivery_details` fields this module reads. Declared structurally rather
 * than imported from the store: `store/ActivityDetail` depends on the URL
 * builders here for its thumbnail view, and a type import would be a cycle.
 */
export interface IVideoDeliveryDetails {
  cloudflare_stream?: string;
  bunny_stream?: string;
  s3?: string;
  youtube?: string;
}

export interface IVideoSource {
  src: string;
  type: string;
}

export const HLS_TYPE = 'application/x-mpegURL';
export const DASH_TYPE = 'application/dash+xml';

const CLOUDFLARE_HOST = 'https://videodelivery.net';

/** The Bunny Stream library's pull zone. One library, so one hostname. */
const BUNNY_HOST = 'https://vz-7edf791c-4ee.b-cdn.net';

/** Master HLS manifest for a Bunny video id. */
export const bunnyPlaylistUrl = (id: string) => `${BUNNY_HOST}/${id}/playlist.m3u8`;

/** Poster frame for a Bunny video id. */
export const bunnyThumbnailUrl = (id: string) => (id ? `${BUNNY_HOST}/${id}/thumbnail.jpg` : '');

/** Poster frame for a Cloudflare Stream video id. */
export const cloudflareThumbnailUrl = (id: string) =>
  id ? `${CLOUDFLARE_HOST}/${id}/thumbnails/thumbnail.jpg?height=270&time=30s` : '';

/**
 * Every playable source for a video, best first. The player starts on
 * `sources[0]` and rotates through the rest when one errors, so the order is
 * the failover order: Bunny (current provider) → Cloudflare (legacy) → our own
 * CDN file → YouTube.
 */
export const getSources = (delivery: IVideoDeliveryDetails): IVideoSource[] => {
  const sources: IVideoSource[] = [];

  const bunny = delivery.bunny_stream;
  if (bunny) {
    sources.push({ src: bunnyPlaylistUrl(bunny), type: HLS_TYPE });
  }

  const cfStream = delivery.cloudflare_stream;
  if (cfStream) {
    sources.push({ src: `${CLOUDFLARE_HOST}/${cfStream}/manifest/video.mpd`, type: DASH_TYPE });
    sources.push({ src: `${CLOUDFLARE_HOST}/${cfStream}/manifest/video.m3u8`, type: HLS_TYPE });
  }

  const cdn = delivery.s3;
  if (cdn) {
    if (cdn.includes('.mp4')) sources.push({ src: cdn, type: 'video/mp4' });
    if (cdn.includes('.webm')) sources.push({ src: cdn, type: 'video/webm' });
  }

  const yt = delivery.youtube;
  if (yt) {
    sources.push({
      src: yt.startsWith('https://') ? yt : `https://www.youtube.com/embed/${yt}`,
      type: 'video/youtube',
    });
  }

  return sources;
};
